import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';

import apiClient from '../../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../../components/GlobalHeaderTitle';
import GlobalDeleteModal from '../../../components/GlobalDeleteModal';
import ClassSectionFilter from '../../Students/parts/ClassSectionFilter';
import AssignBulkTeachersModal from './AssignBulkTeachers';
import ViewTeacherModal, { TeacherRow } from './ViewTeacherModal';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';

// ─────────────────────────────── types ───────────────────────────────

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

interface ClassAssignment {
  id: string;
  class_id: string;
  sections: string[];
  teacher_name: string | null;
  subject_name: string;
}

interface GroupedAssignment {
  subjectName: string;
  assignments: ClassAssignment[];
  totalSections: number;
  assignedCount: number;
}

type Teacher = TeacherRow;

interface Pagination {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
}

type ActiveView = 'class' | 'teacher';

// ─────────────────────────────── helpers ─────────────────────────────

const getInitials = (name: string) =>
  name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

const AVATAR_COLORS = [
  { bg: '#dbeafe', color: '#1e40af' },
  { bg: '#dcfce7', color: '#166534' },
  { bg: '#fef3c7', color: '#92400e' },
  { bg: '#fce7f3', color: '#9d174d' },
  { bg: '#ede9fe', color: '#4c1d95' },
  { bg: '#ffedd5', color: '#9a3412' },
];

const avatarColor = (id: string | number) => {
  const numId = typeof id === 'string' ? parseInt(id) || 0 : id;
  return AVATAR_COLORS[numId % AVATAR_COLORS.length];
};

const STATUS_STYLE = (ratio: number) => {
  if (ratio === 1) return { bg: '#d1fae5', color: '#065f46', dot: '#10b981', label: 'Fully Assigned' };
  if (ratio === 0) return { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', label: 'Unassigned' };
  return { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', label: 'Partial' };
};

const countTotalSections = (assignments: ClassAssignment[]): number => {
  const allSections = new Set<string>();
  assignments.forEach(a => (a.sections ?? []).forEach(s => allSections.add(s)));
  return allSections.size;
};

const getId = (item: any): string => {
  return String(item.id || item._id || item.ID || '');
};

const MAX_SECTION_CHIPS = 4;

// ═════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════

const AssignedTeachersDashboard: React.FC = () => {
  const toast = useRef<Toast>(null);

  // Current session id from school details store
  const currentSessionId = useSchoolDetailsStore(
    (state) => state.schoolDetails?.current_session?.id ?? null
  );

  // ── Tab / view state ──
  const [activeView, setActiveView] = useState<ActiveView>('class');

  // ════════════════════════════════════════
  // CLASS VIEW STATE
  // ════════════════════════════════════════
  const [classAssignments, setClassAssignments] = useState<ClassAssignment[]>([]);
  const [classLoading, setClassLoading] = useState(false);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  // Non-null => GlobalDeleteModal is open for this assignment
  const [deleteTarget, setDeleteTarget] = useState<{ assignment: ClassAssignment; subjectName: string } | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  // Derived class stats
  const grouped: GroupedAssignment[] = (() => {
    const map: Record<string, ClassAssignment[]> = {};
    classAssignments.forEach(a => {
      if (!map[a.subject_name]) map[a.subject_name] = [];
      map[a.subject_name].push(a);
    });
    return Object.entries(map).map(([subjectName, list]) => ({
      subjectName,
      assignments: list,
      totalSections: countTotalSections(list),
      assignedCount: list.filter(a => a.teacher_name).length,
    }));
  })();

  const totalSubjects = grouped.length;
  const fullyAssigned = grouped.filter(g => g.assignedCount === g.assignments.length && g.assignments.length > 0).length;
  const totalSections = classAssignments.reduce((acc, a) => acc + (a.sections?.length ?? 0), 0);
  const assignedSections = classAssignments.filter(a => a.teacher_name).reduce((acc, a) => acc + (a.sections?.length ?? 0), 0);

  // ════════════════════════════════════════
  // TEACHER VIEW STATE
  // ════════════════════════════════════════
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [teacherPagination, setTeacherPagination] = useState<Pagination | null>(null);
  const [teacherLoading, setTeacherLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [teacherPage, setTeacherPage] = useState(1);
  const [teacherLimit, setTeacherLimit] = useState(10);
  const [teacherExpandedRows, setTeacherExpandedRows] = useState<any>(null);

  // ViewTeacherModal state (teacher view)
  const [viewModalTeacher, setViewModalTeacher] = useState<Teacher | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);

  // ════════════════════════════════════════
  // CLASS VIEW EFFECTS & HANDLERS
  // ════════════════════════════════════════

  useEffect(() => {
    if (selectedClass) fetchClassAssignments(selectedClass);
    else setClassAssignments([]);
  }, [selectedClass, currentSessionId]);

  const fetchClassAssignments = async (classId: string) => {
    setClassLoading(true);
    setClassAssignments([]);
    try {
      const res = await apiClient.get(`/api/assigned-teachers/class/${classId}`, {
        params: {
          session_id: currentSessionId || undefined,
        },
      });

      if (res.data.status === 'success') {
        const data = (res.data.assignments || []).map((a: any) => ({
          id: getId(a),
          class_id: String(a.class_id || a.classId || ''),
          sections: a.sections ?? [],
          teacher_name: a.teacher_name || null,
          subject_name: a.subject_name || '',
        }));
        setClassAssignments(data);
        if (data.length === 0) {
          toast.current?.show({
            severity: 'info',
            summary: 'No Data',
            detail: 'No teacher assignments found for this class.',
            life: 3000,
          });
        }
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'No Data',
          detail: res.data.message || 'No assignments found.',
          life: 3000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Failed to fetch assignments.',
        life: 4000,
      });
    } finally {
      setClassLoading(false);
    }
  };

  const handleDelete = (assignment: ClassAssignment, subjectName: string) => {
    if (!assignment.teacher_name) {
      toast.current?.show({
        severity: 'info',
        summary: 'No Assignment',
        detail: 'No teacher is assigned to remove.',
        life: 3000,
      });
      return;
    }
    setDeleteTarget({ assignment, subjectName });
  };

  const handleDeleteModalClose = () => {
    if (deleteTarget && deletingIds.has(deleteTarget.assignment.id)) return;
    setDeleteTarget(null);
  };

  const performDelete = async (assignment: ClassAssignment) => {
    setDeletingIds(prev => new Set(prev).add(assignment.id));
    try {
      const res = await apiClient.delete(`/api/assigned-teachers/${assignment.id}`);
      if (res.data.status === 'success') {
        setClassAssignments(prev => prev.filter(a => a.id !== assignment.id));
        toast.current?.show({
          severity: 'success',
          summary: 'Deleted',
          detail: 'Assignment removed successfully.',
          life: 3000,
        });
      } else {
        throw new Error(res.data.message);
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.message || 'Failed to delete.',
        life: 4000,
      });
    } finally {
      setDeletingIds(prev => {
        const s = new Set(prev);
        s.delete(assignment.id);
        return s;
      });
      setDeleteTarget(null);
    }
  };

  // ════════════════════════════════════════
  // TEACHER VIEW EFFECTS & HANDLERS
  // ════════════════════════════════════════

  const fetchTeachers = useCallback(
    async (pg: number, q: string) => {
      setTeacherLoading(true);
      try {
        const res = await apiClient.get('/api/assigned-teachers/teachers-with-assignments', {
          params: {
            page: pg,
            limit: teacherLimit,
            search: q || undefined,
            session_id: currentSessionId || undefined,
          },
        });

        if (res.data.status === 'success') {
          const teachersData = (res.data.data || []).map((t: any) => ({
            ...t,
            id: getId(t),
            school_id: String(t.school_id || ''),
            assignments: (t.assignments || []).map((a: any) => ({
              ...a,
              id: getId(a),
              class_id: String(a.class_id || ''),
              teacher_id: String(a.teacher_id || ''),
              subject_id: String(a.subject_id || ''),
              session_id: String(a.session_id || ''),
              sections: a.sections || [],
            })),
          }));
          setTeachers(teachersData);
          setTeacherPagination(res.data.pagination);
        } else {
          toast.current?.show({
            severity: 'warn',
            summary: 'Warning',
            detail: res.data.message,
            life: 3000,
          });
        }
      } catch (err: any) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: err.response?.data?.message || 'Failed to fetch teachers.',
          life: 4000,
        });
      } finally {
        setTeacherLoading(false);
      }
    },
    [teacherLimit, currentSessionId]
  );

  useEffect(() => {
    if (activeView === 'teacher') fetchTeachers(teacherPage, search);
  }, [teacherPage, search, activeView, fetchTeachers]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setTeacherPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const openDetail = (teacher: Teacher) => {
    setViewModalTeacher(teacher);
    setViewModalOpen(true);
  };

  // ════════════════════════════════════════
  // CLASS VIEW — COMPACT SUBJECT CARDS
  // ════════════════════════════════════════

  const renderAssignmentRow = (a: ClassAssignment, subjectName: string) => {
    const isDel = deletingIds.has(a.id);
    const sections = a.sections ?? [];
    const visible = sections.slice(0, MAX_SECTION_CHIPS);
    const extra = sections.length - visible.length;
    const assigned = !!a.teacher_name;

    return (
      <div key={a.id} className="atd-assign-row">
        <div
          style={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            flexShrink: 0,
            background: assigned ? '#dcfce7' : '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <i
            className={assigned ? 'pi pi-user' : 'pi pi-user-minus'}
            style={{ fontSize: 10, color: assigned ? '#166534' : '#b45309' }}
          />
        </div>

        <div
          title={a.teacher_name || 'Not assigned'}
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            fontWeight: 600,
            color: assigned ? '#1f2937' : '#d97706',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {a.teacher_name || 'Not assigned'}
        </div>

        <div
          title={sections.join(', ')}
          style={{ display: 'flex', gap: 3, flexShrink: 0, alignItems: 'center' }}
        >
          {sections.length === 0 && <span style={{ fontSize: 10.5, color: '#9ca3af' }}>—</span>}
          {visible.map(sec => (
            <span key={sec} className="atd-sec-chip">
              {sec}
            </span>
          ))}
          {extra > 0 && (
            <span className="atd-sec-chip" style={{ background: '#f3f4f6', color: '#6b7280', borderColor: '#e5e7eb' }}>
              +{extra}
            </span>
          )}
        </div>

        <button
          className="atd-row-btn"
          disabled={isDel}
          onClick={() => (assigned ? handleDelete(a, subjectName) : setIsAssignModalOpen(true))}
          title={assigned ? 'Remove assignment' : 'Assign teacher'}
          style={{
            background: assigned ? '#fee2e2' : '#dbeafe',
            opacity: isDel ? 0.5 : 1,
            cursor: isDel ? 'not-allowed' : 'pointer',
          }}
        >
          {isDel ? (
            <i className="pi pi-spin pi-spinner" style={{ fontSize: 10, color: '#ef4444' }} />
          ) : (
            <i
              className={assigned ? 'pi pi-times' : 'pi pi-plus'}
              style={{ fontSize: 10, color: assigned ? '#ef4444' : '#1e40af' }}
            />
          )}
        </button>
      </div>
    );
  };

  const renderSubjectCard = (g: GroupedAssignment) => {
    const ratio = g.assignments.length > 0 ? g.assignedCount / g.assignments.length : 0;
    const st = STATUS_STYLE(ratio);
    const pillLabel =
      ratio === 1 ? 'Complete' : ratio === 0 ? 'Pending' : `${g.assignedCount}/${g.assignments.length}`;

    return (
      <div key={g.subjectName} className="atd-subject-card">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 10px' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              flexShrink: 0,
              background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <i className="pi pi-book" style={{ color: '#fff', fontSize: 13 }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              title={g.subjectName}
              style={{
                fontWeight: 700,
                fontSize: 13.5,
                color: '#1f2937',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {g.subjectName}
            </div>
            <div
              style={{
                fontSize: 11,
                color: '#9ca3af',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {g.totalSections} section{g.totalSections !== 1 ? 's' : ''} · {g.assignments.length} teacher slot
              {g.assignments.length !== 1 ? 's' : ''}
            </div>
          </div>
          <span
            style={{
              padding: '3px 10px',
              borderRadius: 20,
              background: st.bg,
              color: st.color,
              fontSize: 11,
              fontWeight: 700,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: st.dot,
                display: 'inline-block',
                marginRight: 5,
                verticalAlign: 'middle',
              }}
            />
            {pillLabel}
          </span>
        </div>

        {/* Progress */}
        <div style={{ height: 3, background: '#f3f4f6', margin: '0 14px', borderRadius: 3, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${Math.round(ratio * 100)}%`,
              background: st.dot,
              borderRadius: 3,
              transition: 'width 0.3s ease',
            }}
          />
        </div>

        {/* Assignment rows (always visible, scroll if many) */}
        <div className="atd-assign-list">{g.assignments.map(a => renderAssignmentRow(a, g.subjectName))}</div>
      </div>
    );
  };

  const renderClassEmpty = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: 360,
        background: 'white',
        borderRadius: 20,
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
          borderRadius: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 20,
          boxShadow: '0 6px 18px rgba(59,130,246,0.2)',
        }}
      >
        <i className={!selectedClass ? 'pi pi-filter' : 'pi pi-inbox'} style={{ fontSize: 25, color: 'white' }} />
      </div>
      <h3
        style={{
          margin: 0,
          color: '#1e40af',
          fontSize: 20,
          fontWeight: 700,
          marginBottom: 8,
          textAlign: 'center',
        }}
      >
        {!selectedClass ? 'No Class Selected' : 'No Assignments Found'}
      </h3>
      <p
        style={{
          margin: 0,
          color: '#64748b',
          fontSize: 14,
          fontWeight: 500,
          textAlign: 'center',
          maxWidth: 400,
          lineHeight: 1.6,
        }}
      >
        {!selectedClass
          ? 'Please select a class from the filter above to view teacher assignments'
          : 'No teacher assignments exist for this class yet'}
      </p>
    </div>
  );

  const renderClassSkeleton = () => (
    <div className="atd-subject-grid">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className="atd-subject-card" style={{ padding: 14 }}>
          <div className="atd-shimmer" style={{ height: 32, width: '60%', borderRadius: 8, marginBottom: 14 }} />
          <div className="atd-shimmer" style={{ height: 22, borderRadius: 6, marginBottom: 8 }} />
          <div className="atd-shimmer" style={{ height: 22, borderRadius: 6, marginBottom: 8 }} />
          <div className="atd-shimmer" style={{ height: 22, borderRadius: 6 }} />
        </div>
      ))}
    </div>
  );

  // ════════════════════════════════════════
  // TEACHER VIEW TEMPLATES
  // ════════════════════════════════════════

  const avatarTemplate = (row: Teacher) => {
    const c = avatarColor(row.id);
    return (
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          flexShrink: 0,
          background: c.bg,
          color: c.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 13,
          margin: '0 auto',
        }}
      >
        {getInitials(row.name)}
      </div>
    );
  };

  const nameTemplate = (row: Teacher) => (
    <div style={{ minWidth: 0 }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: 13,
          color: '#1f2937',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {row.name}
      </div>
      <div
        style={{
          fontSize: 11,
          color: '#9ca3af',
          marginTop: 2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {row.email_id}
      </div>
    </div>
  );

  const summaryTemplate = (row: Teacher) => (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      <Tag value={`${row.summary.total_subjects} subj`} severity="info" style={{ fontSize: 11 }} />
      <Tag value={`${row.summary.total_classes} class`} severity="success" style={{ fontSize: 11 }} />
      <Tag value={`${row.summary.total_sections} sec`} severity="warning" style={{ fontSize: 11 }} />
    </div>
  );

  const genderTemplate = (row: Teacher) => (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: row.gender === 'Male' ? '#dbeafe' : row.gender === 'Female' ? '#fce7f3' : '#f3f4f6',
        color: row.gender === 'Male' ? '#1e40af' : row.gender === 'Female' ? '#9d174d' : '#374151',
        whiteSpace: 'nowrap',
      }}
    >
      {row.gender || '—'}
    </span>
  );

  const teacherActionTemplate = (row: Teacher) => (
    <Button
      label="View"
      icon="pi pi-eye"
      className="p-button-sm p-button-outlined"
      style={{ fontSize: 12 }}
      onClick={() => openDetail(row)}
    />
  );

  const teacherRowExpansionTemplate = (row: Teacher) => (
    <div style={{ padding: '10px 20px 18px', background: '#f9fafb', borderTop: '1px solid #f3f4f6' }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#9ca3af',
          textTransform: 'uppercase',
          letterSpacing: '0.7px',
          marginBottom: 10,
        }}
      >
        Quick overview — {row.name}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 6,
          maxHeight: 280,
          overflowY: 'auto',
          paddingRight: 4,
        }}
      >
        {row.assignments.length === 0 && (
          <div style={{ fontSize: 13, color: '#9ca3af', gridColumn: '1 / -1' }}>No assignments yet.</div>
        )}
        {row.assignments.map(a => (
          <div
            key={a.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              background: '#fff',
              borderRadius: 8,
              padding: '8px 12px',
              border: '1px solid #e5e7eb',
            }}
          >
            <i className="pi pi-book" style={{ color: '#4169E1', fontSize: 13 }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', minWidth: 120 }}>{a.subject_name}</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{a.class_name}</span>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginLeft: 'auto' }}>
              {a.sections.map(sec => (
                <span
                  key={sec}
                  style={{
                    padding: '2px 8px',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    background: '#eff6ff',
                    color: '#1e40af',
                    border: '1px solid #bfdbfe',
                  }}
                >
                  {sec}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // ════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════

  const isClassView = activeView === 'class';

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <style>{`
        @keyframes fadeIn  { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes atdShimmer { 0% { background-position: -300px 0; } 100% { background-position: 300px 0; } }

        .p-datatable .p-datatable-tbody > tr { animation: slideIn 0.4s ease-out; transition: all 0.4s cubic-bezier(0.4,0,0.2,1); }
        .p-datatable .p-datatable-tbody > tr:hover {
          background: linear-gradient(to right, rgba(65,105,225,0.04) 0%, rgba(65,105,225,0.02) 100%) !important;
          box-shadow: inset 4px 0 0 0 #4169E1;
        }
        .p-datatable .p-datatable-thead > tr > th {
          background: #f9fafb !important; color: #6b7280 !important;
          font-size: 11px !important; font-weight: 700 !important;
          text-transform: uppercase !important; letter-spacing: 0.8px !important;
          padding: 14px 12px !important; border-bottom: 1px solid #e5e7eb !important;
          white-space: nowrap;
        }
        .p-datatable .p-datatable-tbody > tr > td { padding: 12px !important; border-bottom: 1px solid #f3f4f6 !important; }
        .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom: none !important; }
        .p-row-toggler { color: #9ca3af !important; }
        .p-row-toggler:hover { color: #4169E1 !important; background: rgba(65,105,225,0.08) !important; border-radius: 6px !important; }
        .p-button-text:hover { background: #f3f4f6 !important; }

        .atd-table-scroll { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }

        /* ── Class view: 2 subjects per row ── */
        .atd-subject-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
          align-items: stretch;
        }
        @media (max-width: 1280px) {
          .atd-subject-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 860px) {
          .atd-subject-grid { grid-template-columns: minmax(0, 1fr); }
        }
        .atd-subject-card {
          background: white; border-radius: 16px; border: 1px solid #e5e7eb;
          box-shadow: 0 2px 10px rgba(0,0,0,0.04); overflow: hidden;
          display: flex; flex-direction: column;
          animation: fadeIn 0.3s ease-out;
          transition: box-shadow 0.2s, border-color 0.2s;
        }
        .atd-subject-card:hover { box-shadow: 0 6px 18px rgba(65,105,225,0.10); border-color: #dbe4ff; }
        .atd-assign-list { flex: 1 1 auto; max-height: 232px; overflow-y: auto; padding: 4px 0 6px; margin-top: 6px; }
        .atd-assign-row {
          display: flex; align-items: center; gap: 8px; padding: 6px 14px;
          transition: background 0.15s;
        }
        .atd-assign-row + .atd-assign-row { border-top: 1px dashed #f3f4f6; }
        .atd-assign-row:hover { background: #f9fafb; }
        .atd-sec-chip {
          display: inline-flex; align-items: center; padding: 1px 7px; border-radius: 20px;
          background: #eff6ff; color: #1e40af; font-size: 10.5px; font-weight: 700;
          border: 1px solid #bfdbfe; line-height: 1.5;
        }
        .atd-row-btn {
          width: 24px; height: 24px; border-radius: 6px; border: none; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; transition: transform 0.15s;
        }
        .atd-row-btn:hover:not(:disabled) { transform: scale(1.1); }
        .atd-shimmer {
          background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%);
          background-size: 600px 100%; animation: atdShimmer 1.2s infinite linear;
        }

        .atd-stat-card {
          background: white; border-radius: 16px; padding: 18px 20px;
          border: 1px solid #e5e7eb; flex: 1 1 130px; min-width: 130px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .atd-stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(65,105,225,0.1); }

        .atd-tab-btn {
          display: inline-flex; align-items: center; gap: 7px;
          padding: 8px 20px; border-radius: 10px; border: 1.5px solid #e5e7eb;
          font-size: 13px; font-weight: 600; cursor: pointer;
          transition: all 0.18s; background: #fff; color: #6b7280;
        }
        .atd-tab-btn.active {
          background: linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%);
          color: #fff; border-color: #4169E1;
          box-shadow: 0 4px 14px rgba(65,105,225,0.25);
        }
        .atd-tab-btn:not(.active):hover { background: #f9fafb; color: #1f2937; border-color: #d1d5db; }

        .atd-search-wrapper {
          display: flex; align-items: center; gap: 10px; background: white;
          border-radius: 12px; padding: 10px 16px; box-shadow: 0 2px 8px rgba(30,64,175,0.06);
          border: 1px solid rgba(59,130,246,0.12); transition: border-color .2s, box-shadow .2s;
          flex: 1 1 320px; max-width: 420px;
        }
        .atd-search-wrapper:focus-within { border-color: rgba(59,130,246,0.4); box-shadow: 0 2px 12px rgba(30,64,175,0.12); }
        .atd-search-wrapper input { flex: 1; border: none; outline: none; font-size: 13px; font-weight: 500; color: #1f2937; background: transparent; }
        .atd-search-wrapper input::placeholder { color: #9ca3af; font-weight: 400; }
        .atd-search-clear { background: none; border: none; cursor: pointer; color: #9ca3af; padding: 2px 4px; border-radius: 4px; display: flex; align-items: center; transition: color .2s; }
        .atd-search-clear:hover { color: #6b7280; }
      `}</style>

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        {/* ── Header ── */}
        <GlobalHeaderTitle
          title={isClassView ? 'Assigned Teachers' : 'Teacher Assignments'}
          subtitle={
            isClassView
              ? `${totalSubjects} subjects · ${totalSections} sections`
              : 'View and manage subjects assigned to each teacher'
          }
          icon={isClassView ? 'pi pi-users' : 'pi pi-user-edit'}
          buttons={
            isClassView
              ? [
                  {
                    label: 'Refresh',
                    icon: classLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh',
                    onClick: () => selectedClass && fetchClassAssignments(selectedClass),
                    outlined: true,
                    disabled: classLoading || !selectedClass,
                    style: { borderWidth: '2px' },
                  },
                  {
                    label: 'Assign Teacher',
                    icon: 'pi pi-user-plus',
                    onClick: () => setIsAssignModalOpen(true),
                  },
                ]
              : [
                  {
                    label: 'Refresh',
                    icon: teacherLoading ? 'pi pi-spin pi-spinner' : 'pi pi-refresh',
                    onClick: () => fetchTeachers(teacherPage, search),
                    outlined: true,
                    disabled: teacherLoading,
                    style: { borderWidth: '2px' },
                  },
                ]
          }
        />

        {/* ── View Toggle Tabs ── */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          <button
            className={`atd-tab-btn${isClassView ? ' active' : ''}`}
            onClick={() => setActiveView('class')}
          >
            <i className="pi pi-th-large" style={{ fontSize: 13 }} />
            By Class
          </button>
          <button
            className={`atd-tab-btn${!isClassView ? ' active' : ''}`}
            onClick={() => {
              setActiveView('teacher');
              fetchTeachers(teacherPage, search);
            }}
          >
            <i className="pi pi-users" style={{ fontSize: 13 }} />
            By Teacher
          </button>
        </div>

        {/* ════════════════ CLASS VIEW ════════════════ */}
        {isClassView && (
          <>
            <ClassSectionFilter
              selectedClass={selectedClass}
              selectedSection={selectedSection}
              onClassChange={classId => {
                setSelectedClass(classId);
                setClassAssignments([]);
              }}
              onSectionChange={setSelectedSection}
              onClassesLoaded={setClasses}
            />

            {/* Stat Cards */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
              {[
                {
                  label: 'Total Subjects',
                  value: totalSubjects,
                  icon: 'pi-book',
                  color: '#4169E1',
                  bg: 'rgba(65,105,225,0.08)',
                },
                {
                  label: 'Fully Assigned',
                  value: fullyAssigned,
                  icon: 'pi-check-circle',
                  color: '#10b981',
                  bg: '#d1fae5',
                },
                {
                  label: 'Total Sections',
                  value: totalSections,
                  icon: 'pi-th-large',
                  color: '#6366f1',
                  bg: '#eef2ff',
                },
                {
                  label: 'Sections Assigned',
                  value: assignedSections,
                  icon: 'pi-user',
                  color: '#f59e0b',
                  bg: '#fef3c7',
                },
              ].map(s => (
                <div key={s.label} className="atd-stat-card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 9,
                        background: s.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <i className={`pi ${s.icon}`} style={{ color: s.color, fontSize: 16 }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937' }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{s.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Subject cards — 2 per row */}
            {classLoading ? (
              renderClassSkeleton()
            ) : grouped.length === 0 ? (
              renderClassEmpty()
            ) : (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                    {grouped.length} subject{grouped.length !== 1 ? 's' : ''}
                  </span>
                  <div
                    style={{
                      padding: '5px 12px',
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, rgba(65,105,225,0.1) 0%, rgba(91,124,230,0.08) 100%)',
                      border: '1px solid rgba(65,105,225,0.15)',
                    }}
                  >
                    <span style={{ fontSize: 12.5, color: '#4169E1', fontWeight: 600 }}>
                      {assignedSections}/{totalSections} sections assigned
                    </span>
                  </div>
                </div>
                <div className="atd-subject-grid">{grouped.map(renderSubjectCard)}</div>
              </>
            )}
          </>
        )}

        {/* ════════════════ TEACHER VIEW ════════════════ */}
        {!isClassView && (
          <>
            {/* Search bar */}
            <div
              style={{
                display: 'flex',
                gap: 10,
                marginBottom: 20,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div className="atd-search-wrapper">
                <i className="pi pi-search" style={{ fontSize: 15, color: '#3b82f6', flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Search by name, email or phone…"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
                {searchInput && (
                  <button
                    className="atd-search-clear"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                      setTeacherPage(1);
                    }}
                  >
                    <i className="pi pi-times" style={{ fontSize: 12 }} />
                  </button>
                )}
              </div>
              {teacherPagination && (
                <span style={{ fontSize: 13, color: '#9ca3af', marginLeft: 'auto' }}>
                  {teacherPagination.total_items} teacher{teacherPagination.total_items !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Teacher Table */}
            <div
              style={{
                background: '#fff',
                borderRadius: 20,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
              }}
            >
              <div className="atd-table-scroll">
                <DataTable
                  value={teachers}
                  loading={teacherLoading}
                  expandedRows={teacherExpandedRows}
                  onRowToggle={e => setTeacherExpandedRows(e.data)}
                  rowExpansionTemplate={teacherRowExpansionTemplate}
                  dataKey="id"
                  emptyMessage={
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '60px 20px',
                      }}
                    >
                      <i className="pi pi-inbox" style={{ fontSize: 36, color: '#d1d5db', marginBottom: 14 }} />
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                        No teachers found
                      </div>
                      <div style={{ fontSize: 13, color: '#9ca3af' }}>Try adjusting your search</div>
                    </div>
                  }
                >
                  <Column expander style={{ width: 48 }} />
                  <Column
                    header="#"
                    body={(_: Teacher, opts: any) => (
                      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 12, color: '#9ca3af' }}>
                        {(teacherPage - 1) * teacherLimit + opts.rowIndex + 1}
                      </div>
                    )}
                    style={{ width: 52 }}
                  />
                  <Column header="" body={avatarTemplate} style={{ width: 60 }} />
                  <Column header="Teacher" body={nameTemplate} style={{ minWidth: 200 }} />
                  <Column header="Gender" body={genderTemplate} style={{ width: 100 }} />
                  <Column header="Assignments" body={summaryTemplate} style={{ minWidth: 220 }} />
                  <Column
                    header="Contact"
                    body={(row: Teacher) => (
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{row.contact_no || '—'}</span>
                    )}
                    style={{ minWidth: 130 }}
                  />
                  <Column header="Actions" body={teacherActionTemplate} style={{ width: 100 }} />
                </DataTable>
              </div>
            </div>

            {/* Custom Pagination */}
            {teacherPagination && teacherPagination.total_pages > 1 && (
              <CustomPaginator
                page={teacherPage}
                totalPages={teacherPagination.total_pages}
                totalRecords={teacherPagination.total_items}
                pageSize={teacherLimit}
                onPageChange={newPage => setTeacherPage(newPage)}
                onPageSizeChange={newSize => {
                  setTeacherLimit(newSize);
                  setTeacherPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <ViewTeacherModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        teacher={viewModalTeacher}
        onChanged={() => fetchTeachers(teacherPage, search)}
      />

      <GlobalDeleteModal
        open={deleteTarget !== null}
        onClose={handleDeleteModalClose}
        onConfirm={() => deleteTarget && performDelete(deleteTarget.assignment)}
        loading={!!deleteTarget && deletingIds.has(deleteTarget.assignment.id)}
        title="Remove Teacher Assignment"
        subtitle="This teacher will be unassigned from the subject for the selected sections. This action cannot be undone."
        confirmLabel="Remove"
      >
        {deleteTarget && (
          <div
            style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <i className="pi pi-user" style={{ fontSize: 13, color: '#166534' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1f2937' }}>
                  {deleteTarget.assignment.teacher_name}
                </div>
                <div style={{ fontSize: 11.5, color: '#9ca3af', fontWeight: 500 }}>
                  Subject: {deleteTarget.subjectName}
                </div>
              </div>
            </div>
            {(deleteTarget.assignment.sections ?? []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginRight: 2 }}>Sections:</span>
                {deleteTarget.assignment.sections.map(sec => (
                  <span key={sec} className="atd-sec-chip">
                    {sec}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </GlobalDeleteModal>

      <AssignBulkTeachersModal
        open={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSuccess={() => {
          setIsAssignModalOpen(false);
          if (selectedClass) fetchClassAssignments(selectedClass);
        }}
      />
    </>
  );
};

// ═════════════════════════════════════════════════════════════════════
// CUSTOM PAGINATOR COMPONENT
// ═════════════════════════════════════════════════════════════════════

interface CustomPaginatorProps {
  page: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
  pageSizeOptions?: number[];
  showInfoBar?: boolean;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const CustomPaginator: React.FC<CustomPaginatorProps> = ({
  page,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showInfoBar = true,
}) => {
  if (totalPages <= 1 && !onPageSizeChange) return null;

  const startRecord = (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalRecords);

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, page + 2);

    if (page <= 3) end = Math.min(totalPages, 5);
    if (page >= totalPages - 2) start = Math.max(1, totalPages - 4);

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <>
      {showInfoBar && (
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid #f3f4f6',
            background: '#fafbfc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '12px',
            borderRadius: '0 0 20px 20px',
            border: '1px solid #e5e7eb',
            borderTop: 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, rgba(65,105,225,0.1) 0%, rgba(91,124,230,0.08) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <i className="pi pi-list" style={{ fontSize: '14px', color: '#4169E1' }} />
            </div>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
              Showing {startRecord} - {endRecord} of {totalRecords} entries
            </span>
          </div>
          <div
            style={{
              padding: '8px 16px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)',
              boxShadow: '0 4px 12px rgba(65,105,225,0.3)',
            }}
          >
            <span style={{ fontSize: '13px', color: 'white', fontWeight: '700' }}>
              Page {page} of {totalPages}
            </span>
          </div>
        </div>
      )}

      <div
        style={{
          background: '#fafbfc',
          borderTop: showInfoBar ? 'none' : '1px solid #e5e7eb',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          borderRadius: '0 0 20px 20px',
          border: '1px solid #e5e7eb',
        }}
      >
        {/* Left: per-page selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onPageSizeChange ? (
            <>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Rows per page</span>
              <select
                value={pageSize}
                onChange={e => {
                  onPageSizeChange(Number(e.target.value));
                  onPageChange(1);
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  fontSize: '13px',
                  color: '#1f2937',
                  fontWeight: '600',
                  background: 'white',
                  cursor: 'pointer',
                  outline: 'none',
                }}
              >
                {pageSizeOptions.map(opt => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>{pageSize} per page</span>
          )}
        </div>

        {/* Center: page buttons */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Button
              icon="pi pi-angle-double-left"
              onClick={() => onPageChange(1)}
              disabled={page === 1}
              className="p-button-rounded p-button-text"
              style={{
                width: '40px',
                height: '40px',
                color: page === 1 ? '#cbd5e1' : '#64748b',
                border: '1px solid #e2e8f0',
                background: page === 1 ? '#f8fafc' : 'white',
              }}
            />
            <Button
              icon="pi pi-angle-left"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 1}
              className="p-button-rounded p-button-text"
              style={{
                width: '40px',
                height: '40px',
                color: page === 1 ? '#cbd5e1' : '#64748b',
                border: '1px solid #e2e8f0',
                background: page === 1 ? '#f8fafc' : 'white',
              }}
            />

            {generatePageNumbers().map((p, idx) =>
              typeof p === 'string' ? (
                <span key={idx} style={{ padding: '0 12px', color: '#94a3b8', fontWeight: '600' }}>
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  label={`${p}`}
                  onClick={() => onPageChange(p as number)}
                  className="p-button-rounded"
                  style={{
                    minWidth: '40px',
                    height: '40px',
                    background: p === page ? 'linear-gradient(135deg, #4169E1 0%, #5B7CE6 100%)' : 'white',
                    color: p === page ? 'white' : '#475569',
                    border: p === page ? 'none' : '1px solid #e2e8f0',
                    fontWeight: '600',
                    boxShadow: p === page ? '0 4px 12px rgba(65,105,225,0.4)' : 'none',
                  }}
                />
              )
            )}

            <Button
              icon="pi pi-angle-right"
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
              className="p-button-rounded p-button-text"
              style={{
                width: '40px',
                height: '40px',
                color: page === totalPages ? '#cbd5e1' : '#64748b',
                border: '1px solid #e2e8f0',
                background: page === totalPages ? '#f8fafc' : 'white',
              }}
            />
            <Button
              icon="pi pi-angle-double-right"
              onClick={() => onPageChange(totalPages)}
              disabled={page === totalPages}
              className="p-button-rounded p-button-text"
              style={{
                width: '40px',
                height: '40px',
                color: page === totalPages ? '#cbd5e1' : '#64748b',
                border: '1px solid #e2e8f0',
                background: page === totalPages ? '#f8fafc' : 'white',
              }}
            />
          </div>
        )}

        {/* Right: go-to input */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>Go to</span>
            <input
              type="number"
              min="1"
              max={totalPages}
              value={page}
              onChange={e => {
                const val = parseInt(e.target.value);
                if (val >= 1 && val <= totalPages) onPageChange(val);
              }}
              onKeyDown={e => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
              style={{
                width: '60px',
                padding: '8px 10px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontSize: '13px',
                textAlign: 'center',
                outline: 'none',
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AssignedTeachersDashboard;